import type { Loan, LoanStage } from '@/types'
import type { DealScope, FilterState, MetricFilter, RecencyFilter, RecencyWindow } from './types'
import { STAGES } from './types'
import { millionsFigure } from '@/utils/money'
import { deskDateIso, deskStamp } from './deskTime'
import { STAGE_AGING_DAYS, stageAgingExceeded } from '@/utils/dealAge'
import { customValue, loanCity, loanState, termText } from './loanFigures'

const STAGE_RANK: Record<string, number> = Object.fromEntries(
  STAGES.map((stage, index) => [stage, index]),
)

/** LTV and DSCR become required once a written quote goes out — a Pre-screen
 *  Indication is verbal, so the metrics are not yet firm. Typed as LoanStage so
 *  renaming a stage is a compile error rather than a silently disabled check. */
const METRICS_REQUIRED_FROM: LoanStage = 'Post-screen Quote'

export function money(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—'
  if (Math.abs(value) >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`
  // To $10,000, half up — the grid is tied to Backshop to the dollar, and
  // one decimal (or none above $100M) overstated a $17.25M ask as $17.3M.
  if (Math.abs(value) >= 1_000_000) return `$${millionsFigure(value)}M`
  return `$${value.toLocaleString()}`
}

// Every ratio-scale field on Loan (ltv, debtYield, occupancy, capRate...) is
// stored as a decimal — 0.65, not 65 — matching every OTHER pct() in this
// codebase (Committee.tsx, CreditBox.tsx, Screening.tsx, Valuation.tsx,
// AssetManagement.tsx, RiskMonitoringDashboard.tsx, LoanComparison.tsx all
// multiply by 100). This one didn't, so the pipeline grid's LTV/debt-yield
// columns — the numbers a desk actually scans — printed 0.6% for 65% LTV
// while the Command Center's own weighted-average metric, computed
// separately, read correctly. One value, two screens, two different numbers.
//
// `decimals` places, or two for a figure stated to the hundredth: 0.6649 is
// 66.49%, never 66.5%, which a desk checking the deal book's 66% reads as 67%.
// A computed ratio (65.3846...) keeps `decimals`. view_grid.py `_pct` is the
// server twin.
export function pct(value: number | null | undefined, decimals = 1): string {
  if (value == null || Number.isNaN(value)) return '—'
  return `${(value * 100).toFixed(pctPlaces(value, decimals))}%`
}

/** Excel's format for a ratio, in the places `pct` prints it. */
export function pctNumFmt(value: number): string {
  return pctPlaces(value) === 2 ? '0.00%' : '0.0%'
}

/** The places `pct` prints a ratio to (Excel's format follows it). */
export function pctPlaces(value: number, decimals = 1): number {
  const hundredths = Math.round(value * 100 * 100)
  const stated = Math.abs(value * 100 * 100 - hundredths) < 1e-6 && hundredths % 10 !== 0
  return decimals < 2 && stated ? 2 : decimals
}

/**
 * On a loan's money and ratio fields, zero is not a value — it is the absence
 * of one.
 *
 * A deal whose requested amount was never established stores 0, and `money(0)`
 * printed "$0": a desk reading the pipeline saw three borrowers asking for
 * nothing rather than three records with an unanswered question. The same
 * applied to LTV, which printed "0.0%" — a number a credit officer could act
 * on — for a ratio nobody had stated.
 *
 * The backend already draws this line (`_ZERO_AS_MISSING_FIELDS` in
 * `api/v1/loans.py` covers loan_amount, ltv, dscr and funded_amount). This is
 * the same rule on the screen, so lineage and the grid agree about what is
 * missing.
 *
 * Deliberately NOT applied to every number: an occupancy of 0% or a count of 0
 * is a real, recorded reading.
 */
export function presentNumber(value: number | null | undefined): number | null {
  if (value == null || Number.isNaN(value) || value === 0) return null
  return value
}

/** A loan money field, with zero read as "not recorded". */
export function moneyRecorded(value: number | null | undefined): string {
  return money(presentNumber(value))
}

/** A loan ratio field, with zero read as "not recorded". */
export function pctRecorded(value: number | null | undefined, decimals = 1): string {
  return pct(presentNumber(value), decimals)
}

/** When the deal record was created, as an ISO string for sorting and date
 *  filtering (both compare on the leading YYYY-MM-DD). Falls back to the
 *  origination date so a row is never blank when one is set but the other
 *  is not. */
export function createdIso(loan: Loan): string {
  return loan.createdAt || loan.originated || ''
}

/** "YYYY-MM-DD HH:MM" on the desk's New York clock for a stored timestamp. The
 *  desk reads this to tell two inquiries received the same day apart, so the
 *  time is shown, not just the date. A bare date (the origination fallback)
 *  has no time to show and is rendered as-is. The zone is the desk's, not the
 *  browser's, so the grid, a downloaded workbook and a scheduled report print
 *  the same stamp wherever they are read (see `deskTime`). */
export function createdStamp(loan: Loan): string {
  const raw = createdIso(loan)
  if (!raw) return '—'
  if (raw.length <= 10) return raw
  return deskStamp(raw) ?? raw.slice(0, 10)
}

/** When the deal record last changed: its `updated_at`, the version stamp every
 *  save moves (CMP-04). A record never saved since it was created has none, and
 *  its creation is its last change. */
export function lastModifiedIso(loan: Pick<Loan, 'updatedAt' | 'createdAt'>): string {
  return loan.updatedAt || loan.createdAt || ''
}

/** The last change as `createdStamp` prints the creation: "YYYY-MM-DD HH:MM" on
 *  the desk's clock, or a dash. The server twin is `view_grid._modified_stamp`. */
export function lastModifiedStamp(loan: Pick<Loan, 'updatedAt' | 'createdAt'>): string {
  const raw = lastModifiedIso(loan)
  if (!raw) return '—'
  if (raw.length <= 10) return raw
  return deskStamp(raw) ?? raw.slice(0, 10)
}

/** The recency choices, in the order a picker lists them. */
export const RECENCY_OPTIONS: ReadonlyArray<{ value: RecencyFilter; label: string }> = [
  { value: 'added:today', label: 'Added today' },
  { value: 'added:7d', label: 'Added in the last 7 days' },
  { value: 'added:30d', label: 'Added in the last 30 days' },
  { value: 'modified:today', label: 'Modified today' },
  { value: 'modified:7d', label: 'Modified in the last 7 days' },
  { value: 'modified:30d', label: 'Modified in the last 30 days' },
]

const RECENCY_DAYS: Record<RecencyWindow, number> = { today: 1, '7d': 7, '30d': 30 }

export function isRecencyFilter(value: unknown): value is RecencyFilter {
  return RECENCY_OPTIONS.some((option) => option.value === value)
}

export function recencyLabel(value: RecencyFilter): string {
  return RECENCY_OPTIONS.find((option) => option.value === value)?.label ?? value
}

/**
 * True when the deal was added (`createdIso`, the Date added column) or last
 * changed (`lastModifiedIso`, the Last modified column) within the window: today,
 * or today and the 6 or 29 days before it, on the desk's calendar — the day the
 * grid prints. A deal with no such date is not recent. Anything that is not a
 * recency choice keeps every deal.
 */
export function withinRecency(loan: Loan, recency: unknown, now: Date = new Date()): boolean {
  if (!isRecencyFilter(recency)) return true
  const [field, window] = recency.split(':') as ['added' | 'modified', RecencyWindow]
  const day = deskDateIso(field === 'added' ? createdIso(loan) : lastModifiedIso(loan))
  if (!day) return false
  const today = deskDateIso(now.toISOString())
  if (!today) return true
  // Calendar arithmetic on the desk day itself, at noon UTC so no zone moves it.
  const first = new Date(`${today}T12:00:00Z`)
  first.setUTCDate(first.getUTCDate() - (RECENCY_DAYS[window] - 1))
  return day >= first.toISOString().slice(0, 10)
}

/** Disbursed on a closed loan — NOT the total commitment (see `commitmentSplit`). */
export function fundedAmount(loan: Loan): number | null {
  if (loan.fundedAmount != null && !Number.isNaN(loan.fundedAmount)) return loan.fundedAmount
  return null
}

/** True when the platform computed this figure rather than a document stating it. */
export function isDerivedField(loan: Loan, field: string): boolean {
  return (loan.derivedFields ?? []).includes(field)
}

/** The formula a CURRENT computation derived `field` by, if one did. The API
 *  keys `derived_formulas` by catalogue field; nested keys arrive camel-cased. */
export function derivedFormula(loan: Loan, field: string): string | null {
  const formulas = loan.derivedFormulas ?? {}
  const camel = field.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())
  return formulas[field] ?? formulas[camel] ?? null
}

/** The basis a derived figure is shown with — its formula, and that no
 *  document states it — or null for a figure that is not derived. The grid
 *  marked a derived LTV and left a derived DSCR unmarked (nd3-10). */
export function derivedBasis(loan: Loan, field: string): string | null {
  if (!isDerivedField(loan, field)) return null
  const formula = derivedFormula(loan, field)
  return `Derived${formula ? ` — ${formula}` : ''}; no document states this figure`
}

function customAmount(loan: Loan, key: string): number | null {
  const value = loan.customFields?.[key]
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

/**
 * The commitment as the catalogue records it: initial advance, future funding
 * and total commitment. A leg the other two determine is filled by the identity
 * initial + future = total; a leg nothing settles is null. The grid's "Fully
 * funded" column read `fundedAmount` — money disbursed on a closed loan — so
 * every pipeline deal showed "—" there, and a deal's total commitment appeared
 * nowhere on the pipeline.
 *
 * One definition with the pipeline report (`reporting/builder._commitment_split`):
 * a deal that records no split is its loan amount advanced at close (future
 * funding, which nothing states, is null — never $0), and a
 * deal that records only future funding keeps its stated loan amount as the
 * total — the initial advance is never invented. The grid read "—" where the
 * report printed the loan amount.
 */
export function commitmentSplit(loan: Loan): CommitmentFigures {
  return readCommitment(loan).split
}

export type CommitmentFigures = {
  initial: number | null
  future: number | null
  total: number | null
}

/** Each commitment leg's "calc" basis: null for a figure the record states,
 *  else why the figure is not a stated one (see `commitmentBasis`). */
export type CommitmentBasis = {
  initial: string | null
  future: string | null
  total: string | null
}

/** The basis of a commitment leg the browser fills by the commitment identity. */
function identityBasis(formula: string): string {
  return `Derived — ${formula}; no document states this figure`
}

/** A deal that states no split: its loan amount is shown as the initial
 *  advance and as the total commitment, which no document says it is. */
export const NO_SPLIT_BASIS = 'Derived — no split is stated; shown as the loan amount'

/** A deal that states future funding only: its loan amount is shown as the
 *  total commitment, which no document says it is. */
export const NO_TOTAL_BASIS = 'Derived — no total commitment is stated; shown as the loan amount'

/**
 * One reading of the commitment, with each leg's basis beside its figure, so
 * the figures and their "calc" marks cannot disagree. `totalIsLoanAmount` is
 * set where the total is the loan amount standing in for an unstated one.
 */
function readCommitment(loan: Loan): {
  split: CommitmentFigures
  basis: CommitmentBasis
  totalIsLoanAmount: boolean
} {
  // The API's custom_fields arrive camel-cased through `toCamelCase`.
  let initial = customAmount(loan, 'initialFunding')
  let future = customAmount(loan, 'futureFunding')
  let total = customAmount(loan, 'fullyFundedAmount')
  // A leg the record holds is its own value: unmarked when a document stated
  // it, marked with the engine's formula when the derivation engine wrote it.
  const basis: CommitmentBasis = {
    initial: initial == null ? null : derivedBasis(loan, 'initial_funding'),
    future: future == null ? null : derivedBasis(loan, 'future_funding'),
    total: total == null ? null : derivedBasis(loan, 'fully_funded_amount'),
  }
  // 0 is the NOT NULL column's "no package stated a loan amount".
  const loanAmount = presentNumber(loan.loanAmount)
  if (initial == null && future == null && total == null) {
    return loanAmount == null
      ? { split: { initial: null, future: null, total: null }, basis, totalIsLoanAmount: false }
      : {
          split: { initial: loanAmount, future: null, total: loanAmount },
          basis: { initial: NO_SPLIT_BASIS, future: null, total: NO_SPLIT_BASIS },
          totalIsLoanAmount: true,
        }
  }
  if (total == null && initial != null && future != null) {
    total = initial + future
    basis.total = identityBasis('initial advance plus future funding')
  }
  if (initial == null && total != null && future != null && total >= future) {
    initial = total - future
    basis.initial = identityBasis('total commitment minus future funding')
  }
  if (future == null && total != null && initial != null && total >= initial) {
    future = total - initial
    basis.future = identityBasis('total commitment minus initial advance')
  }
  let totalIsLoanAmount = false
  if (total == null && initial == null && loanAmount != null) {
    total = loanAmount
    basis.total = NO_TOTAL_BASIS
    totalIsLoanAmount = true
  }
  return { split: { initial, future, total }, basis, totalIsLoanAmount }
}

/**
 * Why each figure `commitmentSplit` returns is not a stated one, or null where
 * the record states it. The grid showed a leg it computed (future = total −
 * initial) and the loan amount standing in for an unstated split as if a
 * document had stated them; a client reading "derived vs stated" off the grid
 * could not tell them apart.
 */
export function commitmentBasis(loan: Loan): CommitmentBasis {
  return readCommitment(loan).basis
}

/**
 * The amount a deal asks for, on ONE basis across the book: its total
 * commitment (initial advance + future funding) where the split is recorded,
 * else the loan amount, which is then the whole commitment. `loan_amount`
 * alone is not one basis: Calloway's holds its $68M initial advance of a
 * $71.12M commitment while The Ardsley's holds its $62M total, so the
 * "Requested" column and the "$1.26B Requested" sum mixed the two (OVR-19).
 * Null where no package stated a loan (the NOT NULL column's 0) — never $0.
 */
export function requestedCommitment(loan: Loan): number | null {
  return commitmentSplit(loan).total ?? presentNumber(loan.loanAmount)
}

/**
 * The "calc" basis of `requestedCommitment`, or null for a stated figure. Where
 * the request is the loan amount (no total is stated), it is the loan amount
 * the package stated, and is marked only if the platform derived that; where
 * it is the total commitment, it carries the total's basis.
 */
export function requestedBasis(loan: Loan): string | null {
  const { split, basis, totalIsLoanAmount } = readCommitment(loan)
  if (split.total != null && !totalIsLoanAmount) return basis.total
  return presentNumber(loan.loanAmount) == null ? null : derivedBasis(loan, 'loan_amount')
}

/** Street, city or state is blank — a visible gap, not a Places guess. */
export function needsAddress(loan: Loan): boolean {
  return !termText(customValue(loan, 'street')) || !loanCity(loan) || !loanState(loan)
}

/** Critical fields required for an operable origination record. */
export function criticalMissing(loan: Loan): string[] {
  const missing: string[] = []
  if (!loan.property?.trim()) missing.push('property')
  // One counterparty, so one check. Counting sponsor and borrower separately
  // put two entries on the missing list for one unanswered question, and told
  // a deal that had named its sponsor that it was still short a party. A deal
  // that names only its borrowing entity still passes this check, as it
  // always has: counting it short a sponsor would move the Missing-data
  // count, which is the owner's call (other-11), not a display fix.
  if (!counterparty(loan) && !(loan.borrower ?? '').trim()) missing.push('sponsor')
  if (!loan.location?.trim()) missing.push('location')
  if (!loan.type) missing.push('type')
  if (!(loan.loanAmount > 0)) missing.push('loanAmount')
  // Both the legacy and current unassigned options mean no individual owns
  // the deal. Preserve their stored labels while reading them as a gap.
  const assignee = loan.assignee?.trim()
  if (!assignee || assignee === 'Unassigned' || assignee === 'Unassigned/Originations') missing.push('assignee')
  if (!loan.originated?.trim()) missing.push('originated')

  const stageRank = STAGE_RANK[loan.stage] ?? 0
  if (stageRank >= STAGE_RANK[METRICS_REQUIRED_FROM]) {
    if (!(loan.ltv > 0)) missing.push('ltv')
    if (!(loan.dscr > 0)) missing.push('dscr')
  }
  if (loan.stage === 'Funded' && fundedAmount(loan) == null) {
    missing.push('fundedAmount')
  }
  return missing
}

/**
 * How complete the deal record is, as a whole percent — the API's catalogue
 * measure (`field_completeness`), the same figure the Deal Locator and the
 * dossier export report.
 *
 * This used to score eleven hand-picked checks of its own, so one deal read
 * 82% here, 60% in the Deal Locator and 22% in its dossier export, and the
 * debt-yield and documents checks could never pass on an intake deal. Null
 * when the row carries no measure: a figure is not invented for it.
 */
export function completeness(loan: Loan): number | null {
  const measure = loan.fieldCompleteness
  // Round the count once. `pct` arrives already rounded to one decimal, and
  // rounding it again read 26 of 127 (20.47%) as 21% here while the dossier
  // export, which rounds the count once, read 20%.
  if (measure && Number.isFinite(measure.populated) && Number.isFinite(measure.total) && measure.total > 0) {
    return Math.round((100 * measure.populated) / measure.total)
  }
  const pct = measure?.pct
  return typeof pct === 'number' && Number.isFinite(pct) ? Math.round(pct) : null
}

/** What the completeness percentage counts: "30 of 127 catalogue fields". */
export function completenessBasis(loan: Loan): string | null {
  return loan.fieldCompleteness?.basis ?? null
}

/**
 * The assignment clock's verdict for one deal (register F1/F2).
 *
 * A second clock beside stage aging, and the distinction matters: a deal can be
 * two days into Term Sheet Signed and nine business days overdue with the person
 * it was handed to. `underwriting/sla.py` has computed this for every loan read
 * for a while; nothing displayed it, so a breach was invisible unless somebody
 * read the JSON.
 */
export type SlaVerdict = 'escalated' | 'breached' | 'running' | 'idle'

export function slaVerdict(loan: Loan): SlaVerdict {
  const sla = loan.sla
  if (!sla || !sla.running) return 'idle'
  if (sla.escalated) return 'escalated'
  if (sla.breached) return 'breached'
  return 'running'
}

/** Deals a person is late on. Escalated counts as breached — it is a breach
 *  that has gone further, not a separate state the desk can ignore. */
export function slaBreached(loan: Loan): boolean {
  const verdict = slaVerdict(loan)
  return verdict === 'breached' || verdict === 'escalated'
}

/** Credit Box v2.0 check names, as a desk reads them. */
const CREDIT_BOX_CHECK_LABELS: Record<string, string> = {
  max_ltv: 'LTV above program maximum',
  min_debt_yield_stabilized: 'Stabilized debt yield below program minimum',
  min_dscr_amortizing: 'DSCR below program minimum',
  loan_size: 'Loan size outside program range',
  asset_class: 'Asset class outside program',
  geography: 'Geography outside program',
  construction: 'Construction not in program',
}

export function creditBoxCheckLabel(check: string): string {
  return CREDIT_BOX_CHECK_LABELS[check] ?? check.replace(/_/g, ' ')
}

export function loanExceptions(loan: Loan): string[] {
  const issues: string[] = []
  // Credit exceptions are the client's credit box, read from the screen the
  // server recorded at intake — never thresholds written here. This used to
  // test LTV > 70%, DSCR < 1.25x and debt yield < 8%: none of them the
  // client's box (Program 1 is 80% LTV with no DSCR floor), and a second
  // definition beside the Underwriting board's, which reported 0 while this
  // column flagged 8 deals (stress test EXEC-18).
  for (const check of loan.creditBoxExceptions ?? []) {
    // A ratio computed on a stand-in (loan / purchase price, in-place NOI for
    // the stabilized test) says so; a stabilized basis needs no remark
    // (exec-03).
    const basis = (loan.creditBoxBasis ?? []).find((row) => row.check === check)
    issues.push(
      `Credit box: ${creditBoxCheckLabel(check)}${basis?.standIn && basis.text ? ` (${basis.text})` : ''}`,
    )
  }
  // The time in THIS stage (`daysInStatus`) — one rule, shared with the server's saved-view metric.
  if (stageAgingExceeded(loan)) issues.push(`Stage aging above ${STAGE_AGING_DAYS} days`)
  if ((loan.triggers ?? []).some((trigger) => trigger.status === 'overdue')) {
    issues.push('Overdue requirement')
  }
  // Register F1/F2. Distinct from 'Stage aging above 60 days' above: that is
  // how long the deal has been in a stage, this is how long a person has been
  // holding it. Both can be true, and neither implies the other.
  if (slaBreached(loan)) {
    const owner = loan.sla?.owner
    issues.push(
      loan.sla?.escalated
        ? `Assignment escalated${owner ? ` — ${owner}` : ''}`
        : `Assignment overdue${owner ? ` — ${owner}` : ''}`,
    )
  }
  if (criticalMissing(loan).length > 0) issues.push('Critical data missing')
  // A later email states a newer figure a person must accept or keep (rule
  // R5), and questions the platform knows are open on the deal. Both were
  // invisible here, so the column and the Inspector said 'Clear' over them
  // (stress tests INT-08, OVR-09, CRE-26).
  const revisions = loan.pendingRevisions ?? []
  const revised = fieldList(revisions.filter((r) => !r.lenderDraft).map((r) => r.field))
  if (revised) issues.push(`Newer figure pending review — ${revised}`)
  // The lender's own draft terms wait too, but as the desk's proposal — never
  // as the broker's revision of the deal.
  const drafted = fieldList(revisions.filter((r) => r.lenderDraft).map((r) => r.field))
  if (drafted) issues.push(`Lender draft terms pending review — ${drafted}`)
  // Only an item where the sources disagree is a conflict. The sweep's other
  // items are questions a person answers — "no counterparty is stated" read
  // as "Unresolved data conflict — sponsor" beside "Missing field: sponsor"
  // on Cranbury, where nothing disagrees with anything (nd-059).
  const open = loan.dataConflicts ?? []
  const conflicts = open.filter((c) => CONFLICT_KINDS.has(c.kind))
  const questions = open.filter((c) => !CONFLICT_KINDS.has(c.kind))
  if (conflicts.length > 0) {
    const fields = fieldList(conflicts.map((c) => c.field))
    issues.push(`Unresolved data conflict${fields ? ` — ${fields}` : ''}`)
  }
  if (questions.length > 0) {
    const fields = fieldList(questions.map((c) => c.field))
    issues.push(`Open data question${fields ? ` — ${fields}` : ''}`)
  }
  return issues
}

/**
 * The figure the Exceptions column prints: how many entries `loanExceptions` lists, or null where the
 * deal's exceptions are unknown.
 *
 * A deal with no credit box screen (`creditBoxExceptions` null — the server records one at intake and
 * says nothing for a deal it never screened) has had its credit exceptions neither found nor ruled out.
 * Where no other rule lists anything either, "0" — and the green "Clear" beside it — would say the deal
 * was checked and passed. Unknown never prints 0: the column prints the dash, as every unknown figure does.
 * A count above zero is printed as it is: the rules that did run found that many.
 */
export function exceptionReading(loan: Loan): number | null {
  const count = loanExceptions(loan).length
  return count === 0 && loan.creditBoxExceptions == null ? null : count
}

/** Data-question kinds where two sources disagree about one value: a Smart
 *  Review conflict on the record, or a package naming the counterparty more
 *  than one way. Every other kind is a question, not a conflict. */
const CONFLICT_KINDS = new Set(['conflict', 'rival_counterparties'])

/** Distinct field names, readable: `loan_amount` → `loan amount`. */
function fieldList(fields: Array<string | null | undefined>): string {
  return [...new Set(fields.filter((f): f is string => !!f))]
    .map((f) => f.replace(/_/g, ' '))
    .join(', ')
}

export function parseSmartQuery(input: string, current: FilterState): FilterState {
  const query = input.toLowerCase()
  const next: FilterState = { ...current, search: '' }
  const stage = STAGES.find((candidate) => query.includes(candidate.toLowerCase()))
  const assetClass = [
    'office',
    'industrial',
    'multifamily',
    'retail',
    'hospitality',
    'mixed-use',
    'land',
    'senior living',
    'construction',
    'student housing',
  ].find((candidate) => query.includes(candidate))
  const year = query.match(/20\d{2}/)?.[0]

  if (stage) next.stage = stage
  else if (query.includes('funded') || query.includes('closed')) next.stage = 'Funded'
  if (assetClass) {
    next.assetClass = assetClass.replace(/(^|\s)\S/g, (value) => value.toUpperCase())
  }
  if (year) next.year = year
  if (query.includes('missing') || query.includes('incomplete')) next.metric = 'missing'
  else if (query.includes('exception') || query.includes('risk') || query.includes('problem')) {
    next.metric = 'exceptions'
  } else if (query.includes('all deals') || query.trim() === 'all') {
    next.metric = 'all'
  }
  return next
}

/** A deal the desk has killed. `deal_status` is the state; `deadAt` and the rest
 *  of the kill record only say when and why, so status alone decides. A row
 *  with no status (written before the column existed) is live. */
export function isDeadLoan(loan: Pick<Loan, 'dealStatus'>): boolean {
  return (loan.dealStatus ?? '').trim().toLowerCase() === 'dead'
}

/** The scope a view's filters name. Absent — every view saved before the key
 *  existed — and anything unrecognised read the live pipeline, never the dead. */
export function dealScopeOf(filters: Pick<FilterState, 'dealStatus'> | null | undefined): DealScope {
  const raw = String(filters?.dealStatus ?? '').trim().toLowerCase()
  return raw === 'dead' || raw === 'all' ? raw : 'active'
}

/** The kill-reason code a view narrows its dead deals to, or '' for none. */
export function killReasonOf(filters: Pick<FilterState, 'killReason'> | null | undefined): string {
  return String(filters?.killReason ?? '').trim()
}

/**
 * Is this deal inside the view's kill-reason scope? A live deal always is (the scope narrows the
 * dead deals only); a dead one is when no reason is asked for or its recorded reason is the one
 * asked for. A dead deal with no recorded reason is never inside a scope that names one.
 */
export function inKillReasonScope(
  loan: Pick<Loan, 'dealStatus' | 'killReasonCode'>,
  filters: Pick<FilterState, 'killReason'> | null | undefined,
): boolean {
  const reason = killReasonOf(filters)
  if (!reason || !isDeadLoan(loan)) return true
  return (loan.killReasonCode ?? '').trim() === reason
}

/**
 * The stage a dead deal died at. The kill freezes it (`killed_at_stage`), and
 * the deal's own stage is not touched by the kill, so the two agree unless the
 * record was edited afterwards — then the frozen one is the post-mortem's. A
 * frozen value that is not a stage the book knows falls back to the live field
 * rather than counting the deal nowhere.
 */
export function stageAtDeath(loan: Pick<Loan, 'stage' | 'killedAtStage'>): LoanStage {
  const died = (loan.killedAtStage ?? '').trim()
  return (STAGES as string[]).includes(died) ? (died as LoanStage) : loan.stage
}

/** The stage a deal counts under: where it is, or — for a dead deal — where it died. */
export function bookStage(loan: Pick<Loan, 'stage' | 'killedAtStage' | 'dealStatus'>): LoanStage {
  return isDeadLoan(loan) ? stageAtDeath(loan) : loan.stage
}

/**
 * The deal's Secondary Underwriter (Gate 0 F1): the catalogue's `secondary_underwriter`,
 * "" when none is named. Read from that field alone - never the underwriter or the
 * Responsible Party - so a blank stays blank. GET /loans camel-cases the bag; a row
 * that has not been through that is read in its own spelling too.
 */
export function secondaryUnderwriterOf(loan: Loan): string {
  const bag = (loan.customFields ?? {}) as Record<string, unknown>
  const value = bag.secondaryUnderwriter ?? bag.secondary_underwriter
  return typeof value === 'string' ? value.trim() : ''
}

/** The names the book carries as a Secondary Underwriter: once each, alphabetical, never a blank. */
export function distinctSecondaryUnderwriters(loans: Loan[]): string[] {
  return Array.from(new Set(loans.map(secondaryUnderwriterOf).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b),
  )
}

/** The filter value for "the deals that name no analyst" (T-57). */
export const NO_ANALYST = '(No analyst)'

/** The deal's Analyst (`custom_fields.analyst`), "" when none is named. */
export function analystOf(loan: Loan): string {
  const bag = (loan.customFields ?? {}) as Record<string, unknown>
  const value = bag.analyst
  return typeof value === 'string' ? value.trim() : ''
}

/** The names the book carries as an Analyst: once each, alphabetical, never a blank. */
export function distinctAnalysts(loans: Loan[]): string[] {
  return Array.from(new Set(loans.map(analystOf).filter(Boolean))).sort((a, b) => a.localeCompare(b))
}

function matchesAnalyst(loan: Loan, wanted: string | undefined): boolean {
  if (!wanted || wanted === 'All') return true
  const analyst = analystOf(loan)
  if (wanted === NO_ANALYST) return analyst === ''
  return analyst.toLowerCase() === wanted.toLowerCase()
}

export function applyLoanFilters(loans: Loan[], filters: FilterState): Loan[] {
  const query = filters.search.trim().toLowerCase()
  // The scope is enforced here, not trusted to the fetch: an 'active' view can
  // never show a dead deal even if the list it was handed carries one, and a
  // 'dead' view never shows a live one.
  const scope = dealScopeOf(filters)
  const hasSecondaryFilter = !!filters.secondaryUnderwriter && filters.secondaryUnderwriter !== 'All'
  return loans.filter((loan) => {
    if (scope === 'active' && isDeadLoan(loan)) return false
    if (scope === 'dead' && !isDeadLoan(loan)) return false
    // The same narrowing the server applies (`builder._pipeline_loans`): the dead deals the view
    // names, so the grid, the tiles and every export read one set of deals.
    if (!inKillReasonScope(loan, filters)) return false
    if (
      query &&
      ![
        loan.id,
        loan.property,
        loan.location,
        // Search both spellings: the merged field is `sponsor`, but a deal
        // recorded before the merge can still carry its name in `borrower`.
        loan.borrower,
        loan.sponsor,
        loan.assignee,
        loan.product ?? '',
        loan.type,
        loan.brokerName ?? '',
      ].some((value) => String(value).toLowerCase().includes(query))
    ) {
      return false
    }
    if (filters.stage !== 'All' && bookStage(loan) !== filters.stage) return false
    if (filters.assetClass !== 'All' && loan.type !== filters.assetClass) return false
    if (filters.assignee !== 'All' && loan.assignee !== filters.assignee) return false
    if (hasSecondaryFilter && secondaryUnderwriterOf(loan) !== filters.secondaryUnderwriter) return false
    if (!matchesAnalyst(loan, filters.analyst)) return false
    if (filters.year !== 'All' && !(loan.originated || '').startsWith(filters.year)) return false
    if (filters.metric === 'funded' && loan.stage !== 'Funded') return false
    if (filters.metric === 'exceptions' && loanExceptions(loan).length === 0) return false
    if (filters.metric === 'missing' && criticalMissing(loan).length === 0) return false
    if (filters.metric === 'needsAddress' && !needsAddress(loan)) return false
    if (filters.metric === 'sla' && !slaBreached(loan)) return false
    if (!withinRecency(loan, filters.recency)) return false
    return true
  })
}

export function stageTone(stage: LoanStage): string {
  if (stage === 'Funded') return 'border-emerald-400/25 bg-emerald-400/[0.08] text-emerald-300'
  if (stage === 'Rate Lock' || stage.startsWith('Commitment')) {
    return 'border-amber-300/25 bg-amber-300/[0.08] text-amber-200'
  }
  if (stage.startsWith('Application')) return 'border-sky-400/20 bg-sky-400/[0.07] text-sky-300'
  return 'border-stone-500/20 bg-stone-500/[0.08] text-stone-300'
}

export function metricLabel(metric: MetricFilter): string {
  switch (metric) {
    case 'all':
      return 'All deals'
    case 'funded':
      return 'Funded'
    case 'exceptions':
      return 'Exceptions'
    case 'missing':
      return 'Missing data'
    case 'needsAddress':
      return 'Needs address'
    case 'sla':
      return 'Owner overdue'
    default: {
      const _exhaustive: never = metric
      return _exhaustive
    }
  }
}

export function normalizeViewStatus(status: string): 'Published' | 'Draft' {
  return status.toLowerCase() === 'published' ? 'Published' : 'Draft'
}

/**
 * The deal's sponsor, or ''.
 *
 * Never the borrower. `sponsor` is the merged Sponsor / Borrower field the
 * desk edits; `borrower` names the borrowing entity — the SPV that signs the
 * note — since the platform stopped mirroring the sponsor into it (PKG-09).
 * This used to fall back to the borrower, so a deal whose sponsor is contested
 * (blank on the loan, rivals in Smart Review) showed its SPV as the sponsor,
 * beside the "2 rivals" marker, and an Inspector edit of any other field
 * wrote that SPV into the sponsor (other-11).
 */
export function counterparty(loan: Pick<Loan, 'sponsor'>): string {
  return (loan.sponsor ?? '').trim()
}

/**
 * What the Sponsor / Borrower cell prints: the sponsor; else the borrowing
 * entity, labelled as one; else ''. A contested sponsor prints nothing — the
 * rivals marker beside the blank says why — rather than a borrowing entity a
 * reader would take for the answer.
 */
export function counterpartyText(
  loan: Pick<Loan, 'sponsor' | 'borrower' | 'customFields'>,
  sponsorContested = false,
): string {
  const sponsor = counterparty(loan)
  if (sponsor) return sponsor
  if (sponsorContested) return ''
  const entity = borrowingEntity(loan)
  return entity ? `Borrowing entity: ${entity}` : ''
}

/**
 * The entity that signs the note — the SPV — as the package stated it, or ''.
 *
 * Never the sponsor. `borrower` mirrored the sponsor on every deal written
 * before the platform stopped copying it (stress test PKG-09), so a borrower
 * equal to the sponsor is that mirror, not a statement; the catalogue's own
 * Borrowing Entity field leads. The term sheet's borrower signature block and
 * the inspector's party list read this, never `loan.borrower` directly.
 */
export function borrowingEntity(
  loan: Pick<Loan, 'sponsor' | 'borrower' | 'customFields'>,
): string {
  // `loansService` camel-cases nested keys, so the catalogue's
  // `borrowing_entity` arrives as `borrowingEntity`; a deal file read
  // straight off the API keeps the catalogue spelling.
  const custom = loan.customFields ?? {}
  const stated = custom.borrowingEntity ?? custom.borrowing_entity
  if (typeof stated === 'string' && stated.trim()) return stated.trim()
  const borrower = (loan.borrower ?? '').trim()
  const sponsor = (loan.sponsor ?? '').trim()
  return borrower && borrower.toLowerCase() !== sponsor.toLowerCase() ? borrower : ''
}
